import React, { forwardRef } from 'react';
import { motion } from 'framer-motion';
import PropTypes from 'prop-types';
import './Switch.css';

/**
 * Switch Component
 * Toggle switch component
 *
 * @param {Object} props - Component props
 * @param {string} props.label - Switch label
 * @param {boolean} props.checked - Checked state
 * @param {Function} props.onChange - Change handler
 * @param {boolean} props.disabled - Disabled state
 * @param {string} props.size - Size variant: 'sm', 'md', 'lg'
 * @param {string} props.className - Additional CSS classes
 */
const Switch = forwardRef(
  (
    {
      label,
      checked = false,
      onChange,
      disabled = false,
      size = 'md',
      className = '',
      id,
      name,
      ...props
    },
    ref
  ) => {
    const switchId = id || `switch-${Math.random().toString(36).substr(2, 9)}`;

    const containerClasses = `
      switch-container
      ${disabled ? 'switch-container--disabled' : ''}
      ${className}
    `.trim().replace(/\s+/g, ' ');

    const trackClasses = `
      switch-track
      switch-track--${size}
      ${checked ? 'switch-track--checked' : ''}
    `.trim().replace(/\s+/g, ' ');

    const thumbClasses = `
      switch-thumb
      switch-thumb--${size}
    `.trim().replace(/\s+/g, ' ');

    return (
      <div className={containerClasses}>
        <label htmlFor={switchId} className="switch-label-wrapper">
          <input
            ref={ref}
            type="checkbox"
            id={switchId}
            name={name}
            className="switch-input"
            checked={checked}
            onChange={onChange}
            disabled={disabled}
            role="switch"
            aria-checked={checked}
            aria-label={label}
            {...props}
          />

          <motion.div
            className={trackClasses}
            animate={{
              backgroundColor: checked
                ? 'var(--color-primary)'
                : 'var(--color-gray-300)',
            }}
            transition={{ duration: 0.2 }}
          >
            <motion.div
              className={thumbClasses}
              animate={{
                x: checked
                  ? size === 'sm'
                    ? 14
                    : size === 'lg'
                    ? 26
                    : 20
                  : 0,
              }}
              transition={{ type: 'spring', stiffness: 500, damping: 30 }}
            />
          </motion.div>

          {label && <span className="switch-label">{label}</span>}
        </label>
      </div>
    );
  }
);

Switch.displayName = 'Switch';

Switch.propTypes = {
  label: PropTypes.string,
  checked: PropTypes.bool,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  className: PropTypes.string,
  id: PropTypes.string,
  name: PropTypes.string,
};

export default Switch;
